import { ArrowDownToLine, ArrowUpToLine, MailWarning } from "lucide-react";
import type { EmailThreadSummary } from "@/types/email";

type Props = {
  thread: EmailThreadSummary;
  isActive?: boolean;
  onSelect?: () => void;
  leadTitle?: string;
  leadStage?: string;
  leadRegion?: string;
  highlighted?: boolean;
  onDismiss?: () => void;
  dismissDisabled?: boolean;
  onAddToCrm?: () => void;
};

export function EmailThreadListItem({
  thread,
  isActive,
  onSelect,
  leadTitle,
  leadStage,
  leadRegion,
  highlighted,
  onDismiss,
  dismissDisabled,
  onAddToCrm,
}: Props) {
  const direction = thread.direction === "inbound" ? "Inbound" : thread.direction === "outbound" ? "Outbound" : "Mixed";
  const DirectionIcon =
    thread.direction === "inbound" ? ArrowDownToLine : thread.direction === "outbound" ? ArrowUpToLine : MailWarning;
  const lastAt = thread.last_message_at ? formatRelative(thread.last_message_at) : "Activity unknown";
  const participantLabel = thread.participants?.[0] ?? "Unknown participant";
  const hasLead = typeof thread.lead_id === "number" && Number.isFinite(thread.lead_id);

  const containerClasses = `w-full rounded-2xl border px-3 py-2 text-left transition-all duration-200 ${
    isActive
      ? "border-primary/80 bg-primary/15 shadow-[0_0_0_1px_rgba(59,130,246,0.25)]"
      : highlighted
        ? "border-primary/60 bg-primary/10 hover:border-primary/80 hover:bg-primary/15"
        : "border-border bg-background/30 hover:border-primary/50 hover:bg-background/50"
  }`;
  const dismissable = onDismiss && thread.direction !== "outbound";

  return (
    <div className="relative">
      <div
        role="button"
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSelect?.();
          }
        }}
        className={containerClasses}
      >
        <div className="flex items-center justify-between gap-2 text-xs">
          <div className="inline-flex items-center gap-1 text-muted-foreground">
            <DirectionIcon className="h-3.5 w-3.5" />
            <span className="line-clamp-1">{participantLabel}</span>
          </div>
          <span className="text-muted-foreground">{lastAt}</span>
        </div>
        <p className="mt-0.5 line-clamp-1 text-sm font-semibold text-foreground">
          {thread.subject || "(No subject)"}
        </p>
        <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{direction}</p>
        {thread.last_message_preview && (
          <p className="mt-0.5 line-clamp-1 text-[11px] text-muted-foreground">{thread.last_message_preview}</p>
        )}
        <div className="mt-1.5 flex flex-wrap gap-1.5 text-[9px] uppercase tracking-wide">
          {thread.unreplied && (
            <span className="rounded-full border border-destructive/70 bg-destructive/15 px-2 py-0.5 text-destructive">
              Unreplied
            </span>
          )}
          {thread.has_failed && (
            <span className="rounded-full border border-destructive/50 bg-destructive/10 px-1.5 py-0.5 text-destructive">
              Failed
            </span>
          )}
          {thread.followup_needed && (
            <span className="rounded-full border border-warning/60 bg-warning/10 px-1.5 py-0.5 text-warning">
              Follow-up
            </span>
          )}
          {thread.lead_id && (
            <span className="rounded-full border border-info/40 bg-info/10 px-1.5 py-0.5 text-info">
              Lead #{thread.lead_id}
            </span>
          )}
          {thread.contact_id ? (
            <span className="rounded-full border border-success/40 bg-success/10 px-1.5 py-0.5 text-success">
              Already in CRM
            </span>
          ) : onAddToCrm ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onAddToCrm();
              }}
              className="rounded-full border border-primary/50 bg-primary/10 px-1.5 py-0.5 text-primary transition hover:border-primary hover:bg-primary/15"
            >
              Add to CRM
            </button>
          ) : (
            <span className="rounded-full border border-primary/50 bg-primary/10 px-1.5 py-0.5 text-primary">
              Add to CRM
            </span>
          )}
          {hasLead ? (
            <span className="inline-flex rounded-full border border-border bg-background/40 px-1.5 py-0.5 text-foreground/80">
              Lead:&nbsp;
              {leadTitle || `Lead ${thread.lead_id}`}{" "}
              {leadStage && <span className="ml-1 text-muted-foreground">· {leadStage}</span>}
              {leadRegion && <span className="ml-1 text-muted-foreground">· {leadRegion}</span>}
            </span>
          ) : (
            <span className="inline-flex rounded-full border border-border bg-background/50 px-1.5 py-0.5 text-muted-foreground">
              No lead linked
            </span>
          )}
        </div>
      </div>
      {dismissable && (
        <label className="absolute top-3 right-3 z-10 flex items-center gap-1 rounded-full border border-border/60 bg-background/50 px-2 py-0.5 text-[10px] uppercase tracking-wide text-foreground/70 backdrop-blur-sm">
          <input
            type="checkbox"
            aria-label="Dismiss thread"
            checked={false}
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.stopPropagation();
              if (dismissDisabled) return;
              onDismiss?.();
            }}
            disabled={dismissDisabled}
            className="h-3 w-3 rounded border border-border bg-background/40 text-primary accent-primary"
          />
          <span>{dismissDisabled ? "Dismissing…" : "Dismiss"}</span>
        </label>
      )}
    </div>
  );
}

function formatRelative(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const diffMs = date.getTime() - Date.now();
  const diffMinutes = Math.round(diffMs / 60000);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  if (Math.abs(diffMinutes) < 60) {
    return formatter.format(diffMinutes, "minute");
  }
  const diffHours = Math.round(diffMinutes / 60);
  if (Math.abs(diffHours) < 24) {
    return formatter.format(diffHours, "hour");
  }
  const diffDays = Math.round(diffHours / 24);
  return formatter.format(diffDays, "day");
}
